import { BreakpointKey, QFormFieldViewWrapper, QFormFieldWrapper, QLink, QOverlay, QPopover } from "@qamf/lighthouse";
import type { BaseButtonVariant, PopoverPlacement } from "bootstrap-vue-next";
import { computed, defineComponent, nextTick, onMounted, onUnmounted, PropType, reactive, ref, useTemplateRef } from "vue";

interface IDropDownOption {
	text: string;
	isDisabled: boolean;
	value: string | number;
	href: string
}

export default defineComponent({
	name: "DropdownSelection",
	components: {
		QLink,
		QOverlay,
		QPopover,
		QFormFieldWrapper,
		QFormFieldViewWrapper
	},
	props: {
		modelValue: { type: Object as PropType<IDropDownOption>, default: undefined },
		options: { type: Array as PropType<IDropDownOption[]>, default: () => [] },
		iconInputClass: { type: String, default: "bi" },
		hideIcon: { type: Boolean, default: false },
		name: { type: String, default: null },
		spinnerSize: { type: String, default: "md" },
		showControlsHeader: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		readonly: { type: Boolean, default: false },
		hidePopoverOnSubmit: { type: Boolean, default: true },
		placeholder: { type: String, default: "" },
		variant: { type: String as PropType<keyof BaseButtonVariant>, default: "outline-secondary" },
		buttonVariant: { type: String as PropType<keyof BaseButtonVariant>, default: "outline-secondary" },
		size: {
			type: String,
			default: "md",
			validator: (value: string) => ["xs", "sm", "md", "lg", "xl"].indexOf(value) >= 0
		},
		placement: {
			type: String as PropType<PopoverPlacement>,
			default: "bottom",
			validator: (value: string) =>
				[
					"top",
					"bottom",
					"right",
					"left",
					"auto",
					"top-start",
					"top-left",
					"bottom-start",
					"bottom-end",
					"right-start",
					"right-end",
					"left-start",
					"left-end",
					"auto-start",
					"auto-end"
				].indexOf(value) >= 0
		},
		class: { type: String, default: null },
		teleportTo: { type: String, default: undefined },
		customModalClass: { type: String, default: null },
		modalBreakpoint: {
			type: String as PropType<BreakpointKey>,
			default: "isExtraSmall"
		}
	},
	emits: ["update:modelValue", "blur", "focus", "click", "click-option", "close-popover"],
	setup(props, { emit }) {
		const viewWrapper = useTemplateRef<InstanceType<typeof QFormFieldViewWrapper> | null>("viewWrapper");
		const inputElement = useTemplateRef<HTMLElement | null>("inputElement");
		const popoverContent = ref<HTMLElement>();
		const headControls = ref<HTMLElement>();
		const selectedValue = computed({
			get: () => props.modelValue,
			set: (value) => emit("update:modelValue", value)
		});
		const helpers = reactive({
			loading: false,
			pickerKey: ""
		});
		const inputClass = computed(() => {
			return {
				bi: true,
				[props.iconInputClass]: true,
				disabled: props.disabled
			};
		});
		const classProp = computed(() => {
			return props.class;
		});
		const inputValue = computed(() => {
			if (!selectedValue.value) return "";
			return selectedValue.value.text;
		});
		const submitModel = (option: IDropDownOption | undefined = undefined) => {
			selectedValue.value = option;
		};
		const onClickWrapper = async() => {
			if (props.disabled) return;
			await nextTick();
			viewWrapper.value?.openView();
			emit("close-popover");
			emit("click");
		};
		const onBlur = () => {
			emit("blur");
		};
		const onFocus = () => {
			emit("focus");
		};
		const onOptionClick = (option: IDropDownOption) => {
			if (option.isDisabled || Boolean(option.href)) return;
			submitModel(option);
			emit("click-option", option);
		};
		const openPopover = () => {
			onClickWrapper();
		};
		const hidePopover = () => {
			viewWrapper.value?.hideView();
			emit("close-popover");
		};
		const onSelected = (option: IDropDownOption) => {
			submitModel(option);
			if (props.hidePopoverOnSubmit)
				hidePopover();
		};
		const onClickedOutside = (event: MouseEvent) => {
			if (
				(popoverContent.value && popoverContent.value.contains(event.target as Node))
				|| (headControls.value && headControls.value.contains(event.target as Node))
			)
				return;

			hidePopover();
		};
		onMounted(() => {
			helpers.pickerKey = +new Date() + "_" + Math.random() * 1000;
		});
		onUnmounted(() => {
		});
		return {
			helpers,
			inputValue,
			inputElement,
			inputClass,
			selectedValue,
			classProp,
			popoverContent,
			headControls,
			onClickWrapper,
			onClickedOutside,
			onBlur,
			onFocus,
			onOptionClick,
			openPopover,
			hidePopover,
			onSelected
		};
	}
});
